/**
 * Or Many Fine Art — Shopify embed helper [v2026-04-29-a]
 *
 * Drop this script into your Shopify theme (e.g. inside the section/snippet
 * that renders the <iframe> for the print studio). It will:
 *   1. Append the current Shopify locale to the iframe URL on first load.
 *   2. Push language changes into the iframe via postMessage whenever the
 *      Shopify locale changes (e.g. via the language switcher).
 *
 * Usage in your theme:
 *   <iframe id="print-studio" src="https://or-many-fine-art.lovable.app/order"></iframe>
 *   <script src="https://or-many-fine-art.lovable.app/shopify-embed.js" defer></script>

 *
 * The script auto-attaches to any <iframe> whose src points to the
 * lovable.app domain. You can also call window.OrManySync.setLanguage('he').
 */
(function () {
  "use strict";

  var APP_HOST_MATCH = /lovable\.app|or-many-fine-art/i;

  function normalize(raw) {
    if (!raw) return null;
    var lower = String(raw).toLowerCase().trim();
    if (lower === "iw" || lower === "he" || lower.indexOf("he-") === 0 || lower.indexOf("he_") === 0) return "he";
    if (lower === "en" || lower.indexOf("en-") === 0 || lower.indexOf("en_") === 0) return "en";
    return null;
  }

  function detectShopifyLocale() {
    // 1. Shopify global
    if (typeof window.Shopify === "object" && window.Shopify && window.Shopify.locale) {
      var s = normalize(window.Shopify.locale);
      if (s) return s;
    }
    // 2. <html lang="..">
    var htmlLang = normalize(document.documentElement.lang);
    if (htmlLang) return htmlLang;
    // 3. URL path prefix or ?locale=
    try {
      var url = new URL(window.location.href);
      var fromQuery = normalize(url.searchParams.get("locale") || url.searchParams.get("lang"));
      if (fromQuery) return fromQuery;
      var seg = url.pathname.split("/").filter(Boolean)[0];
      var fromPath = normalize(seg);
      if (fromPath) return fromPath;
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function findIframes() {
    return Array.prototype.filter.call(document.querySelectorAll("iframe"), function (f) {
      return f.src && APP_HOST_MATCH.test(f.src);
    });
  }

  function pushLanguageToIframe(iframe, lang) {
    if (!iframe || !lang) return;
    try {
      iframe.contentWindow.postMessage({ type: "lovable:setLanguage", language: lang }, "*");
    } catch (e) {
      /* cross-origin postMessage shouldn't throw, but be safe */
    }
  }

  function toCartAddFallbackUrl(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;
    var validItems = cart.items.filter(function (i) {
      return i && i.id;
    });
    if (!validItems.length) return null;
    var params = new URLSearchParams();
    if (validItems.length === 1) {
      var only = validItems[0];
      params.set("id", String(only.id));
      params.set("quantity", String(only.quantity || 1));
      var properties = only.properties || {};
      Object.keys(properties).forEach(function (key) {
        if (properties[key] != null) {
          params.set("properties[" + key + "]", String(properties[key]));
        }
      });
    } else {
      // Multi-item fallback (main + add-ons): id[]/quantity[] arrays.
      // Properties only attach to the main (first) line item.
      validItems.forEach(function (item, idx) {
        params.append("id[]", String(item.id));
        params.append("quantity[]", String(item.quantity || 1));
        if (idx === 0) {
          var props = item.properties || {};
          Object.keys(props).forEach(function (key) {
            if (props[key] != null) {
              params.append("properties[" + key + "]", String(props[key]));
            }
          });
        }
      });
    }
    return "/cart/add?" + params.toString();
  }

  function log() {
    if (!window.console || typeof window.console.log !== "function") return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[OrMany embed]");
    try {
      window.console.log.apply(window.console, args);
    } catch (e) {
      window.console.log(args.join(" "));
    }
  }

  function hasCaliforniaAjaxCart() {
    return !!(window.ajaxCart && typeof window.ajaxCart.load === "function");
  }

  function initCaliforniaAjaxCart() {
    try {
      if (window.California && typeof window.California.initAjaxCart === "function") {
        log("Initializing California ajax cart");
        window.California.initAjaxCart();
      }
    } catch (e) {
      log("California.initAjaxCart() failed", e);
    }
  }

  function isCartUiOpen() {
    var body = document.body;
    var html = document.documentElement;
    if (body && body.classList) {
      if (body.classList.contains("shopping")) return true;
      if (body.classList.contains("cart-open")) return true;
      if (body.classList.contains("js-drawer-open")) return true;
      if (body.classList.contains("js-drawer-open-right")) return true;
    }
    if (html && html.classList && html.classList.contains("js-drawer-open-right")) return true;
    if (
      document.querySelector(
        ".side-cart.is-open, .side-cart.open, .cart-drawer.is-open, .cart-drawer.active, #CartDrawer.is-open, .drawer--cart.is-open",
      )
    ) {
      return true;
    }
    return false;
  }

  function dispatchToggleEvent(node, type) {
    if (!node) return;
    try {
      node.dispatchEvent(new Event(type, { bubbles: true, cancelable: true }));
    } catch (e) {
      var evt = document.createEvent("Event");
      evt.initEvent(type, true, true);
      node.dispatchEvent(evt);
    }
  }

  function openCartUi(options) {
    options = options || {};
    var avoidAjaxCartLoad = !!options.avoidAjaxCartLoad;
    var avoidTriggerClick = !!options.avoidTriggerClick;

    if (isCartUiOpen()) {
      log("Cart already open");
      return true;
    }

    initCaliforniaAjaxCart();

    // 1) California theme: prefer the real ajaxCart API unless we're in
    //    safe mode after a successful add-to-cart. In that case we avoid
    //    ajaxCart.load() because it may open the drawer and then navigate
    //    to /cart on some California configurations.
    if (!avoidAjaxCartLoad) {
      try {
        if (hasCaliforniaAjaxCart()) {
          log("Opening cart via ajaxCart.load()");
          window.ajaxCart.load();
          return true;
        }
      } catch (e) {
        log("ajaxCart.load() failed", e);
      }
    } else if (hasCaliforniaAjaxCart()) {
      log("Skipping ajaxCart.load() to avoid theme redirect");
    }

    // 2) California theme: prefer the global SideCart instance API
    try {
      if (window.California && window.California.SideCart && typeof window.California.SideCart.open === "function") {
        log("Opening cart via California.SideCart.open()");
        window.California.SideCart.open();
        return true;
      }
    } catch (e) {
      /* ignore */
    }

    // 3) California theme: use its global drawer registry
    try {
      if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.open === "function") {
        log("Opening cart via theme.cartDrawer.open()");
        window.theme.cartDrawer.open();
        return true;
      }
    } catch (e) {
      /* ignore */
    }

    // 4) Click only NON-NAVIGATING drawer toggles, and only when we explicitly
    //    allow it. On some California setups, even a safe-looking trigger can
    //    still continue into a cart-page navigation after opening the drawer.
    if (!avoidTriggerClick) {
      var selectors = [
        '[data-action="open-drawer"][data-drawer-id*="ide-art"]', // sideCart variations
        '[data-action="open-drawer"][data-drawer-id*="art"]',
        '[data-action="open-drawer"][data-drawer-id="sideCart"]',
        '[data-drawer-toggle="cart"]',
      ];

      function isNavigatingTrigger(node) {
        if (!node) return true;
        // Anchor tags that point at the cart page will navigate after click.
        if (node.tagName === "A") {
          var href = node.getAttribute("href") || "";
          if (!href || href === "#" || href.indexOf("#") === 0) return false;
          if (/\/cart(\/|$|\?)/.test(href)) return true;
          // Any other absolute-ish href is risky too.
          if (href.indexOf("/") === 0 || /^https?:/i.test(href)) return true;
        }
        return false;
      }

      for (var i = 0; i < selectors.length; i += 1) {
        var trigger = document.querySelector(selectors[i]);
        if (!trigger) continue;
        if (isNavigatingTrigger(trigger)) {
          log("Skipping navigating trigger", selectors[i]);
          continue;
        }
        try {
          log("Opening cart via selector", selectors[i]);
          trigger.click();
          return true;
        } catch (e) {
          /* ignore */
        }
        try {
          dispatchToggleEvent(trigger, "touchstart");
          dispatchToggleEvent(trigger, "click");
          return true;
        } catch (e) {
          /* ignore */
        }
      }
    } else {
      log("Skipping trigger click path to avoid theme redirect");
    }

    // 5) Last-resort: try to toggle the classes California uses
    if (document.body) {
      log("Falling back to California body classes");
      document.body.classList.add("shopping");
    }
    document.documentElement.classList.add("js-drawer-open", "js-drawer-open-right");
    document.body.classList.add("js-drawer-open", "js-drawer-open-right");
    return !!document.querySelector(".side-cart");
  }

  function refreshCartUi(cart, options) {
    options = options || {};
    var skipBuildEvent = !!options.skipBuildEvent;
    var skipAddedAjaxProduct = !!options.skipAddedAjaxProduct;

    try {
      log("Refreshing cart UI with generic events");
      if (!skipBuildEvent) {
        document.dispatchEvent(new CustomEvent("cart:build", { detail: cart || {} }));
      }
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: cart || {} }));
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart || {} }));
      document.dispatchEvent(new CustomEvent("cart:change", { detail: cart || {} }));
      if (window.jQuery) {
        if (!skipAddedAjaxProduct) {
          try {
            window.jQuery(document.body).trigger("added.ajaxProduct");
          } catch (e) {
            /* ignore */
          }
        }
        try {
          window.jQuery(document.body).trigger("ajaxCart.afterCartLoad", [cart || {}]);
        } catch (e) {
          /* ignore */
        }
        if (!skipBuildEvent) {
          try {
            window.jQuery(document).trigger("cart:build", [cart || {}]);
          } catch (e) {
            /* ignore */
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
  }

  // Refresh the California cart drawer using the theme's own ajaxCart module.
  // ajaxCart.load() calls /cart.js then rebuilds .side-cart-container from the
  // #CartTemplate handlebars template. It does NOT navigate — the previous
  // /cart redirect was caused by clicking the cart link, not by ajaxCart.load.
  // Returns a Promise that resolves once the rebuild has rendered.
  function refreshCaliforniaDrawer() {
    return new Promise(function (resolve) {
      if (!hasCaliforniaAjaxCart() || !window.jQuery) {
        resolve(false);
        return;
      }
      var $ = window.jQuery;
      var settled = false;
      function done(result) {
        if (settled) return;
        settled = true;
        try {
          $(document.body).off("ajaxCart.afterCartLoad", onLoaded);
        } catch (e) {
          /* ignore */
        }
        resolve(result);
      }
      function onLoaded() {
        // Drawer markup just got rebuilt by ajaxCart.buildCart()
        log("ajaxCart.afterCartLoad fired");
        done(true);
      }
      try {
        $(document.body).on("ajaxCart.afterCartLoad", onLoaded);
      } catch (e) {
        /* ignore */
      }
      try {
        log("Calling ajaxCart.load() to rebuild drawer");
        window.ajaxCart.load();
      } catch (e) {
        log("ajaxCart.load() threw", e);
        done(false);
        return;
      }
      // Safety timeout — never hang the UI if the theme event never fires.
      setTimeout(function () {
        done(false);
      }, 2500);
    });
  }

  // ── Resolution Enhancement reconciliation ────────────────────────────
  // The enhancement is a per-image charge: one enhancement line per unique
  // _image_key, regardless of how many copies of that image are in the cart.
  // After every cart change we:
  //   1) Remove enhancement lines whose image is no longer in the cart.
  //   2) Collapse duplicate enhancement lines for the same image to one.
  //   3) Reset any enhancement quantity > 1 back to 1.
  var __reconcileInFlight = false;
  var __reconcileQueued = false;
  function isEnhancementLine(item) {
    if (!item || !item.properties) return false;
    var role = item.properties._role || item.properties.role;
    return role === "enhancement";
  }
  function getImageKey(item) {
    if (!item || !item.properties) return "";
    return String(item.properties._image_key || item.properties.image_key || "").toLowerCase();
  }
  function changeCartLine(key, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }
  function reconcileEnhancementLines() {
    if (__reconcileInFlight) {
      __reconcileQueued = true;
      return Promise.resolve();
    }
    __reconcileInFlight = true;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;
        var printKeys = {};
        cart.items.forEach(function (it) {
          if (!isEnhancementLine(it)) {
            var k = getImageKey(it);
            if (k) printKeys[k] = true;
          }
        });
        var seenEnhancement = {};
        var ops = [];
        cart.items.forEach(function (it) {
          if (!isEnhancementLine(it)) return;
          var k = getImageKey(it);
          // Orphan: parent image no longer in cart
          if (!k || !printKeys[k]) {
            log("Removing orphan enhancement line", it.key, "imageKey=", k);
            ops.push(changeCartLine(it.key, 0));
            return;
          }
          // Duplicate: another enhancement for same image already kept
          if (seenEnhancement[k]) {
            log("Removing duplicate enhancement for image", k);
            ops.push(changeCartLine(it.key, 0));
            return;
          }
          seenEnhancement[k] = true;
          // Quantity normalisation: enhancement is always qty 1
          if (Number(it.quantity) !== 1) {
            log("Normalising enhancement qty to 1 for image", k);
            ops.push(changeCartLine(it.key, 1));
          }
        });
        if (!ops.length) return null;
        return Promise.all(ops).then(function () {
          // Refresh drawer once after mutations so the user sees the result
          return refreshCaliforniaDrawer().catch(function () {
            return null;
          });
        });
      })
      .catch(function (e) {
        log("reconcileEnhancementLines failed", e);
        return null;
      })
      .then(function () {
        __reconcileInFlight = false;
        if (__reconcileQueued) {
          __reconcileQueued = false;
          // Run once more to absorb changes that happened during the run
          setTimeout(reconcileEnhancementLines, 50);
        }
      });
  }

  function handleCheckoutMessage(data) {
    if (!data || data.type !== "ormany:checkout-drawer") return;
    log("Received ormany:checkout-drawer message");

    var cart = data.cart;
    if (!cart || !cart.items || !cart.items.length) {
      log("No cart payload found, redirecting to fallback URL");
      if (data.url) window.location.href = data.url;
      return;
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(cart),
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Cart add failed");
        log("/cart/add.js succeeded");

        // Reconcile enhancement lines (dedupe per image, remove orphans).
        // We do this BEFORE rebuilding the drawer so the user sees the
        // final, correct state.
        return reconcileEnhancementLines().then(function () {
          initCaliforniaAjaxCart();

          if (hasCaliforniaAjaxCart()) {
            log("California ajaxCart detected — rebuilding drawer then opening");
            // Rebuild via the theme's own renderer (uses /cart.js + #CartTemplate),
            // then open the drawer via the safe non-click path so we never trigger
            // a navigation to /cart.
            return refreshCaliforniaDrawer().then(function () {
              try {
                openCartUi({
                  avoidAjaxCartLoad: true, // we already loaded above
                  avoidTriggerClick: true,
                });
              } catch (e) {
                /* ignore */
              }
              return null;
            });
          }

          return fetch("/cart.js", { headers: { Accept: "application/json" } });
        }); // end reconcileEnhancementLines().then
      })
      .then(function (cartResponse) {
        if (!cartResponse) return null;
        log("/cart.js refresh request succeeded");
        return cartResponse.json().catch(function () {
          log("Failed parsing /cart.js response");
          return null;
        });
      })
      .then(function (cartJson) {
        if (!cartJson) return;
        // Generic (non-California) themes: dispatch refresh events, then open.
        refreshCartUi(cartJson);
        setTimeout(function () {
          try {
            openCartUi();
          } catch (e) {
            /* ignore */
          }
          setTimeout(function () {
            try {
              openCartUi();
            } catch (e) {
              /* ignore */
            }
          }, 400);
        }, 80);
      })
      .catch(function (error) {
        log("Cart bridge failed, falling back", error);
        var fallbackUrl = data.url || toCartAddFallbackUrl(cart);
        if (fallbackUrl) window.location.href = fallbackUrl;
      });
  }

  function syncAll(lang) {
    if (!lang) lang = detectShopifyLocale();
    if (!lang) return;
    findIframes().forEach(function (iframe) {
      // Also append to URL on first load so the app knows immediately
      try {
        var u = new URL(iframe.src);
        if (u.searchParams.get("lang") !== lang) {
          u.searchParams.set("lang", lang);
          // Avoid reloading if we don't have to: only set src if not yet loaded
          if (!iframe.dataset.lovableLangApplied) {
            iframe.src = u.toString();
            iframe.dataset.lovableLangApplied = "1";
          }
        }
      } catch (e) {
        /* ignore */
      }
      pushLanguageToIframe(iframe, lang);
    });
  }

  // Respond to the iframe asking for our language on boot
  window.addEventListener("message", function (event) {
    var data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "ormany:checkout-drawer") {
      handleCheckoutMessage(data);
      return;
    }
    if (data.type === "lovable:requestLanguage") {
      var lang = detectShopifyLocale();
      if (lang && event.source && event.source.postMessage) {
        try {
          event.source.postMessage({ type: "lovable:setLanguage", language: lang }, "*");
        } catch (e) {
          /* ignore */
        }
      }
    }
  });

  // Initial sync once iframes load
  function attachLoadHandlers() {
    findIframes().forEach(function (iframe) {
      iframe.addEventListener("load", function () {
        var lang = detectShopifyLocale();
        if (lang) pushLanguageToIframe(iframe, lang);
      });
    });
  }

  // Watch for Shopify locale changes (theme switcher / SPA navigation)
  var lastLang = detectShopifyLocale();
  function watchLocale() {
    var current = detectShopifyLocale();
    if (current && current !== lastLang) {
      lastLang = current;
      syncAll(current);
    }
  }
  setInterval(watchLocale, 800);
  window.addEventListener("popstate", watchLocale);
  document.addEventListener("visibilitychange", watchLocale);

  // Listen for cart-change events from the theme drawer (e.g. user clicks
  // the X on a print line). When the cart changes we run reconciliation so
  // orphaned enhancement lines are auto-removed and duplicates collapsed.
  function scheduleReconcile() {
    setTimeout(function () {
      reconcileEnhancementLines();
    }, 50);
  }
  ["cart:updated", "cart:refresh", "cart:change", "cart:build"].forEach(function (evt) {
    document.addEventListener(evt, scheduleReconcile);
  });
  if (window.jQuery) {
    try {
      window.jQuery(document.body).on("ajaxCart.afterCartLoad", scheduleReconcile);
    } catch (e) {
      /* ignore */
    }
  }

  // ── Stable, key-based remove for the California cart drawer ─────────
  // The theme's built-in X button removes lines by positional index. After
  // the bridge adds multi-item carts (print + addon), those indices drift
  // and Shopify can return "line parameter is invalid" — or, worse, the
  // drawer re-renders against a stale cart and appears to "delete the
  // whole cart" or the wrong line.
  //
  // We intercept clicks on remove controls inside the side cart, look up
  // the matching line key from /cart.js by line index, and remove via
  // /cart/change.js with that key. Then we refresh the drawer and run the
  // enhancement reconciliation pass.
  var REMOVE_SELECTOR = [
    ".side-cart .cart__remove",
    ".side-cart [data-cart-remove]",
    '.side-cart a[href*="/cart/change"]',
    '.side-cart a[href*="/cart/?line"]',
    '.side-cart a[href*="quantity=0"]',
    ".cart-drawer .cart__remove",
    ".cart-drawer [data-cart-remove]",
    '.side-cart-container a[href*="/cart/change"]',
    ".side-cart-container [data-cart-remove]",
  ].join(",");

  function findCartRowFromTarget(node) {
    var cur = node;
    while (cur && cur !== document.body) {
      if (
        cur.matches &&
        (cur.matches("[data-cart-item]") ||
          cur.matches("[data-line]") ||
          cur.matches("[data-line-item]") ||
          cur.matches("[data-key]") ||
          cur.matches(".cart__row, .cart-item, .side-cart__item, .line-item"))
      )
        return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function findLineKeyForRow(row, container) {
    if (!row) return { key: null, index: null };
    // 1) Stable key on row
    var k = row.getAttribute("data-key") || row.getAttribute("data-line-key") || row.getAttribute("data-cart-key");
    if (k) return { key: k, index: null };
    // 2) Anchor href like ?line=N or quantity=0&line=N
    var anchor = row.querySelector('a[href*="line="], a[href*="/cart/change"]');
    if (anchor) {
      var href = anchor.getAttribute("href") || "";
      var lm = href.match(/[?&]line=(\d+)/);
      if (lm) return { key: null, index: Number(lm[1]) };
    }
    // 3) data-line attribute
    var dl = row.getAttribute("data-line") || row.getAttribute("data-line-index");
    if (dl) return { key: null, index: Number(dl) };
    // 4) Fallback: positional index among rendered rows in this container
    if (container) {
      var rows = container.querySelectorAll("[data-cart-item], .cart__row, .cart-item, .side-cart__item, .line-item");
      for (var i = 0; i < rows.length; i += 1) {
        if (rows[i] === row) return { key: null, index: i + 1 };
      }
    }
    return { key: null, index: null };
  }

  document.addEventListener(
    "click",
    function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var trigger = target.closest(REMOVE_SELECTOR);
      if (!trigger) return;
      var container = trigger.closest(".side-cart, .side-cart-container, .cart-drawer, #CartDrawer, .drawer--cart");
      if (!container) return;

      log("Intercepted cart remove click", trigger);
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      var row = findCartRowFromTarget(trigger);
      var hint = findLineKeyForRow(row, container);

      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;
          var item = null;
          if (hint.key) {
            item =
              cart.items.find(function (it) {
                return String(it.key) === String(hint.key);
              }) || null;
          }
          if (!item && hint.index != null && hint.index >= 1 && hint.index <= cart.items.length) {
            item = cart.items[hint.index - 1];
          }
          if (!item) {
            log("Could not resolve line key for click — falling back to default theme handler");
            return null;
          }
          log("Removing cart line by key", item.key, "qty 0");
          return changeCartLine(item.key, 0);
        })
        .then(function () {
          // Refresh drawer markup, then reconcile addon lines.
          return refreshCaliforniaDrawer().catch(function () {
            return null;
          });
        })
        .then(function () {
          return reconcileEnhancementLines();
        })
        .catch(function (err) {
          log("Key-based remove failed", err);
        });
    },
    true,
  );

  // Boot
  function boot() {
    attachLoadHandlers();
    syncAll();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Public API
  window.OrManySync = {
    setLanguage: function (lang) {
      var n = normalize(lang);
      if (n) syncAll(n);
    },
    detect: detectShopifyLocale,
  };
})();
